import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'src/components/layouts/Modal/Modal';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Checkbox from 'src/components/ui/Checkbox/Checkbox';
import Icon from 'src/components/ui/Icon/Icon';
import SafeHtml from 'src/components/ui/SafeHtml/SafeHtml';
import { localeDate } from 'src/localization/locale';
import {
  ChangelogAttachment,
  ChangelogEntry,
  downloadChangelogAttachment,
  fetchChangelogAssetUrl,
} from 'src/services/changelog';
import styles from './ChangelogModal.module.scss';

const formatSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

const AttachmentRow: React.FC<{ attachment: ChangelogAttachment }> = ({ attachment }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const onDownload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await downloadChangelogAttachment(attachment);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-12 rounded-base border-subtle surface-base px-12 py-8">
      <Icon name={attachment.is_image ? 'imageUpload' : 'file'} width={18} height={18} className="text-faded" />
      <div className="flex-1 min-w-0">
        <div className="typo-text-14--medium text-full truncate">{attachment.name}</div>
        <div className="typo-text-12 text-faded">{formatSize(attachment.size)}</div>
      </div>
      <Button
        tone={ButtonToneProps.SECONDARY}
        icon="download"
        title={busy ? '…' : t('changelog.download')}
        onClick={onDownload}
        disabled={busy}
      />
    </div>
  );
};

/** MP4 attachments play inline; the bytes are fetched with the bearer token first. */
const AttachmentVideo: React.FC<{ attachment: ChangelogAttachment }> = ({ attachment }) => {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    fetchChangelogAssetUrl(attachment.url, controller.signal)
      .then(url => {
        objectUrl = url;
        setSrc(url);
      })
      .catch(() => setSrc(null));
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.url]);
  if (!src) return null;
  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <video className={styles.video} src={src} controls preload="metadata" />;
};

const Lightbox: React.FC<{ src: string; alt: string; onClose: () => void }> = ({ src, alt, onClose }) => (
  <Modal isOpen onClose={onClose}>
    <div className="p-16 flex items-center justify-center">
      <img src={src} alt={alt} className="max-h-[80vh] max-w-full object-contain rounded-base" />
    </div>
  </Modal>
);

export interface ChangelogModalProps {
  entries: ChangelogEntry[];
  isOpen: boolean;
  /** Called on every explicit close (X, Escape, backdrop, "Got it") with the "don't show again" choice. */
  onClose: (dontShowAgain: boolean) => void;
  /** Called when the user moves to another entry; receives the entry being left. */
  onEntryLeft?: (entry: ChangelogEntry) => void;
  /** Shows the "don't show again" checkbox in the footer. */
  showDontShowAgain?: boolean;
}

const ChangelogModal: React.FC<ChangelogModalProps> = ({
  entries,
  isOpen,
  onClose,
  onEntryLeft,
  showDontShowAgain = false,
}) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    setIndex(0);
    setDontShowAgain(false);
  }, [entries]);

  const entry = entries[index];
  if (!isOpen || !entry) return null;
  const last = index === entries.length - 1;
  const videos = entry.attachments.filter(a => a.is_video);
  const files = entry.attachments.filter(a => !a.is_video);

  const goTo = (next: number) => {
    onEntryLeft?.(entry);
    setIndex(next);
  };

  const close = () => onClose(dontShowAgain);

  return (
    <>
      <Modal isOpen={isOpen} onClose={close} hasCloseButton={false}>
        <div className={styles.frame}>
          <div className={styles.header}>
            <div className="flex flex-col gap-4 min-w-0">
              <span className="typo-text-12--bold text-faded uppercase">{t('changelog.whats_new')}</span>
              <h4 className="typo-title-24 text-full">{entry.title}</h4>
              <span className="typo-text-12 text-faded">
                {[entry.version, entry.publish_date ? localeDate(entry.publish_date) : null]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label={t('general.close')}
              title={t('general.close')}
              className={styles.close}>
              <Icon name="close" width={18} height={18} />
            </button>
          </div>

          <div className={styles.body}>
            {entry.video_embed_url && (
              <div className={styles.embed}>
                <iframe
                  src={entry.video_embed_url}
                  title={entry.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            )}
            {videos.map(video => (
              <AttachmentVideo key={video.id} attachment={video} />
            ))}
            <SafeHtml html={entry.body_html} onImageClick={(src, alt) => setLightbox({ src, alt })} />
            {files.length > 0 && (
              <div className="flex flex-col gap-8">
                <span className="typo-text-12--bold text-faded uppercase">{t('changelog.attachments')}</span>
                {files.map(file => (
                  <AttachmentRow key={file.id} attachment={file} />
                ))}
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <div className="flex items-center gap-16">
              <span className="typo-text-12 text-faded">
                {entries.length > 1 ? t('changelog.step', { current: index + 1, total: entries.length }) : ''}
              </span>
              {showDontShowAgain && (
                <div className="flex items-center gap-8">
                  <Checkbox checked={dontShowAgain} onChange={e => setDontShowAgain(e.target.checked)} />
                  <span className="typo-text-14 text-full">{t('changelog.dont_show_again')}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-8">
              {index > 0 && (
                <Button tone={ButtonToneProps.SECONDARY} title={t('changelog.back')} onClick={() => goTo(index - 1)} />
              )}
              {last ? (
                <Button tone={ButtonToneProps.PRIMARY_BLUE} title={t('changelog.got_it')} onClick={close} />
              ) : (
                <Button
                  tone={ButtonToneProps.PRIMARY_BLUE}
                  title={t('changelog.next')}
                  onClick={() => goTo(index + 1)}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>
      {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
    </>
  );
};

export default ChangelogModal;
